// Safari Performance and Memory Optimizations

interface SafariOptimizations {
  enableLazyImages: boolean;
  enableBundleSplitting: boolean;
  enableMemoryOptimization: boolean;
  enableNetworkOptimization: boolean;
}

// Safari memory constraints (especially on older iPhones)
const SAFARI_MEMORY_LIMITS = {
  iPhone6: 1024 * 1024 * 1024, // 1GB
  iPhone7: 2 * 1024 * 1024 * 1024, // 2GB
  iPhone8: 2 * 1024 * 1024 * 1024, // 2GB
  iPhoneX: 3 * 1024 * 1024 * 1024, // 3GB
  default: 512 * 1024 * 1024 // 512MB for older devices
};

// Detect Safari/iOS version for optimization decisions
const detectSafariVersion = (): { version: number; isIOS: boolean; memoryLimit: number } => {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  
  let version = 0;
  let memoryLimit = SAFARI_MEMORY_LIMITS.default;
  
  if (isIOS) {
    // Extract iOS version
    const match = userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      version = parseInt(match[1]);
      
      // Estimate memory based on iOS version (rough approximation)
      if (version >= 14) memoryLimit = SAFARI_MEMORY_LIMITS.iPhoneX;
      else if (version >= 11) memoryLimit = SAFARI_MEMORY_LIMITS.iPhone8;
      else if (version >= 10) memoryLimit = SAFARI_MEMORY_LIMITS.iPhone7;
      else if (version >= 9) memoryLimit = SAFARI_MEMORY_LIMITS.iPhone6;
    }
  } else {
    // Desktop Safari
    const match = userAgent.match(/Version\/(\d+)/);
    if (match) {
      version = parseInt(match[1]);
      memoryLimit = SAFARI_MEMORY_LIMITS.iPhoneX; // Desktop has more memory
    }
  }
  
  return { version, isIOS, memoryLimit };
};

// Memory optimization for Safari
let memoryCheckInterval: NodeJS.Timeout | null = null;

export const enableSafariMemoryOptimization = () => {
  const safariInfo = detectSafariVersion();
  
  // Only enable aggressive memory optimization on older/low-memory devices
  if (safariInfo.isIOS && safariInfo.version < 12) {
    console.log('[Safari] Enabling aggressive memory optimization for older iOS');
    
    // Clear caches more frequently
    memoryCheckInterval = setInterval(() => {
      try {
        // Clear unused images
        const images = document.querySelectorAll('img');
        images.forEach(img => {
          if (!img.getBoundingClientRect().width) {
            img.src = '';
          }
        });
        
        // Clear unused React components (if possible)
        if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
          // Force garbage collection if dev tools are available
          (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = () => {};
        }
        
        // Check performance memory (if available)
        if ((performance as any).memory) {
          const memory = (performance as any).memory;
          const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
          
          if (usagePercent > 80) {
            console.warn('[Safari] High memory usage detected:', usagePercent.toFixed(1) + '%');
            
            // Suggest page reload if memory is critically high
            if (usagePercent > 90) {
              console.error('[Safari] Critical memory usage - suggesting reload');
              
              // Show user notification
              if (window.confirm('The app is using a lot of memory. Would you like to refresh for better performance?')) {
                window.location.reload();
              }
            }
          }
        }
      } catch (error) {
        console.warn('[Safari] Memory optimization error:', error);
      }
    }, 30000); // Check every 30 seconds
  }
};

// Image lazy loading for Safari
export const enableSafariLazyImages = () => {
  // Use IntersectionObserver if available, fallback to scroll events
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px'
    });
    
    // Observe all images with data-src
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => imageObserver.observe(img));
    
    console.log('[Safari] Lazy image loading enabled with IntersectionObserver');
  } else {
    // Fallback for older Safari
    const lazyLoad = () => {
      const images = document.querySelectorAll('img[data-src]');
      images.forEach(img => {
        const rect = img.getBoundingClientRect();
        if (rect.top < (window as Window).innerHeight + 50) {
          (img as HTMLImageElement).src = img.getAttribute('data-src') || '';
          img.removeAttribute('data-src');
        }
      });
    };

    (window as Window).addEventListener('scroll', lazyLoad, { passive: true } as AddEventListenerOptions);
    (window as Window).addEventListener('resize', lazyLoad, { passive: true } as AddEventListenerOptions);
    lazyLoad(); // Initial load

    console.log('[Safari] Lazy image loading enabled with scroll events');
  }
};

// Network optimizations for Safari
export const enableSafariNetworkOptimization = () => {
  // Preload critical resources
  const preloadCriticalResources = () => {
    const criticalResources = [
      '/static/css/main.css',
      '/static/js/main.js'
    ];
    
    criticalResources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = resource.endsWith('.css') ? 'style' : 'script';
      document.head.appendChild(link);
    });
  };
  
  // DNS prefetch for external resources
  const prefetchDNS = () => {
    const domains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://www.gstatic.com',
      'https://firebaseapp.com'
    ];
    
    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      preloadCriticalResources();
      prefetchDNS();
    });
  } else {
    preloadCriticalResources();
    prefetchDNS();
  }
  
  console.log('[Safari] Network optimizations enabled');
};

// Bundle splitting optimization for Safari
export const enableSafariBundleSplitting = () => {
  // This is mainly handled by webpack configuration, but we can provide hints
  
  // Dynamic import polyfill for older Safari
  if (!('import' in document.createElement('script'))) {
    console.log('[Safari] Dynamic imports not supported, using fallback loading');
    
    // Override dynamic imports with traditional script loading
    (window as any).__dynamicImportPolyfill__ = (modulePath: string) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = modulePath;
        script.onload = () => {
          const moduleName = modulePath.split('/').pop()?.replace('.js', '') || '';
          resolve((window as any)[moduleName]);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };
  }
};

// Main Safari optimization function
export const initSafariOptimizations = (options: Partial<SafariOptimizations> = {}) => {
  const defaultOptions: SafariOptimizations = {
    enableLazyImages: true,
    enableBundleSplitting: true,
    enableMemoryOptimization: true,
    enableNetworkOptimization: true
  };
  
  const config = { ...defaultOptions, ...options };
  const safariInfo = detectSafariVersion();
  
  console.log('[Safari] Initializing optimizations for Safari', safariInfo.version, 'iOS:', safariInfo.isIOS);
  
  try {
    if (config.enableMemoryOptimization) {
      enableSafariMemoryOptimization();
    }
    
    if (config.enableLazyImages) {
      enableSafariLazyImages();
    }
    
    if (config.enableNetworkOptimization) {
      enableSafariNetworkOptimization();
    }
    
    if (config.enableBundleSplitting) {
      enableSafariBundleSplitting();
    }
    
    // Set Safari-specific CSS variables
    document.documentElement.style.setProperty('--safari-version', safariInfo.version.toString());
    document.documentElement.style.setProperty('--is-ios', safariInfo.isIOS ? '1' : '0');
    
    // Add Safari-specific classes
    document.body.classList.add('safari-optimized');
    if (safariInfo.isIOS) {
      document.body.classList.add('ios-optimized');
      document.body.classList.add(`ios-${safariInfo.version}`);
    }
    
    console.log('[Safari] All optimizations initialized successfully');
    return true;
  } catch (error) {
    console.error('[Safari] Error initializing optimizations:', error);
    return false;
  }
};

// Cleanup function
export const cleanupSafariOptimizations = () => {
  if (memoryCheckInterval) {
    clearInterval(memoryCheckInterval);
    memoryCheckInterval = null;
  }
  
  console.log('[Safari] Optimizations cleaned up');
};

// Performance monitoring for Safari
export const monitorSafariPerformance = () => {
  const startTime = performance.now();
  let lastMemoryCheck = 0;
  
  const checkPerformance = () => {
    const currentTime = performance.now();
    const timeSinceStart = currentTime - startTime;
    
    // Check memory every 5 minutes
    if (currentTime - lastMemoryCheck > 300000) {
      lastMemoryCheck = currentTime;
      
      if ((performance as any).memory) {
        const memory = (performance as any).memory;
        console.log('[Safari] Performance check:', {
          uptime: Math.round(timeSinceStart / 1000) + 's',
          memoryUsed: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
          memoryLimit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + 'MB',
          memoryPercent: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100) + '%'
        });
      }
    }
  };
  
  setInterval(checkPerformance, 60000); // Check every minute
  console.log('[Safari] Performance monitoring started');
};

export default {
  initSafariOptimizations,
  cleanupSafariOptimizations,
  monitorSafariPerformance,
  detectSafariVersion
}; 